import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'reactstrap';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import { DetailHeader } from '../../../Components/Common/Detail';
import '../../../Components/Common/Detail/detail.css';
import '../../TenderOpening/components/TenderOpening.css';
import '../Negotiation.css';
import useNegotiationDetail from '../hooks/useNegotiationDetail';
import useNegotiationClock from '../hooks/useNegotiationTimer';
import NegotiationWorkspace from './NegotiationWorkspace';
import NegotiationRoom from './NegotiationRoom';
import NegotiationStatusBadge from '../components/NegotiationStatusBadge';
import {
  getPartnerDisplayStatus,
  isCargoRunning,
} from '../utils/negotiationStatus';
import {
  displayValue,
  formatTimerDisplay,
  priceUnitSuffix,
  remainingSeconds,
} from '../utils/formatters';
import { formatDateTime } from '../../../utils/format';
import { buildOfferFacts, buildProcurementHeader } from '../utils/offerHeader';
import { EmptyState } from '../../../Components/Common/ListStates';
import { SkeletonTable } from '../../../Components/Common/Skeleton';

// Route `:id` = procurementId (GUID). Workspace <-> room is local state; a
// room is one partner of one cargo.
const NegotiationDetail = ({
  mode = 'buyer',
  listPath = '/apps-negotiation/view',
  endpoint = '/negotiation',
  canStartTimer = true,
  breadcrumbLabel = 'Negotiation',
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activePartnerId, setActivePartnerId] = useState(null);
  // Picked alpha line per partner room (cleared on leaving the room, so a
  // reopen recomputes the default), and the cargo table partner filter
  // (buyer, kept while the page is open).
  const [lineByPartner, setLineByPartner] = useState({});
  const [partnerFilter, setPartnerFilter] = useState(null);

  const {
    detail,
    isLoading,
    refresh,
    startCargo,
    extendCargo,
    submitRound,
    acceptLine,
    withdrawRound,
    withdrawPartner,
  } = useNegotiationDetail(id, { endpoint });

  const refreshSilent = useCallback(() => refresh({ silent: true }), [refresh]);
  // On a deadline the BE resolves OVERDUE on read, so just refetch.
  const nowMs = useNegotiationClock(detail, refreshSilent);

  document.title =
    mode === 'supplier' ? 'Negotiation Detail' : 'Negotiation (Buyer) Detail';

  const active = useMemo(() => {
    if (!activePartnerId) return null;
    for (const cargo of detail?.cargoes || []) {
      const partner = cargo.partners.find(
        p => String(p.id) === String(activePartnerId)
      );
      if (partner) return { cargo, partner };
    }
    return null;
  }, [activePartnerId, detail?.cargoes]);
  const activeCargo = active?.cargo;
  const activePartner = active?.partner;

  const selectLine = useCallback(
    lineId =>
      setLineByPartner(prev =>
        activePartnerId ? { ...prev, [activePartnerId]: lineId } : prev
      ),
    [activePartnerId]
  );

  const procurementHeader = detail?.procurement
    ? buildProcurementHeader(detail.procurement)
    : null;
  const headerFacts = procurementHeader
    ? buildOfferFacts({
        procurement:
          activePartner?.kind === 'counter' &&
          activePartner.proposalValidityPeriodMaxCounter
            ? {
                ...procurementHeader,
                proposalValidityPeriodMax:
                  activePartner.proposalValidityPeriodMaxCounter,
              }
            : procurementHeader,
        sellerName: activePartner?.sellerName,
      })
    : [];

  const handleBack = () => {
    if (activePartnerId) {
      setLineByPartner(prev => {
        const next = { ...prev };
        delete next[activePartnerId];
        return next;
      });
      setActivePartnerId(null);
      // Opening the room marked its chat read; refresh the unread badges.
      refreshSilent();
    } else {
      navigate(listPath);
    }
  };

  const timerRunning = isCargoRunning(activeCargo);
  const timerLeft = timerRunning
    ? remainingSeconds(activeCargo.deadline, nowMs)
    : 0;
  const timerExpired = timerRunning && timerLeft <= 0;
  const timerClass = [
    'neg-hero-timer',
    timerExpired ? 'is-expired' : '',
    activeCargo?.startedAt && !timerRunning ? 'is-done' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const deadlineText = activeCargo?.deadline
    ? `${formatDateTime(activeCargo.deadline)} WIB`
    : null;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='neg-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{ label: breadcrumbLabel, to: listPath }}
                    current={
                      activePartner
                        ? 'Room'
                        : `Detail # ${displayValue(procurementHeader?.procurementNumber)}`
                    }
                  />

                  <DetailHeader
                    onBack={handleBack}
                    backLabel={
                      activePartnerId ? 'Back to workspace' : 'Back to list'
                    }
                    loading={isLoading && !detail}
                    title={
                      !detail && !isLoading
                        ? 'Not found'
                        : procurementHeader?.titleProcurement || breadcrumbLabel
                    }
                    subtitle={
                      !detail && !isLoading
                        ? 'Negotiation data not found'
                        : activePartner?.label || null
                    }
                    eyebrow={
                      detail ? (
                        <>
                          <span className='neg-chip'>
                            <i className='ri-hashtag' aria-hidden='true' />
                            {displayValue(procurementHeader?.procurementNumber)}
                          </span>
                          {activePartner?.idOffer ? (
                            <span className='neg-chip'>
                              <i
                                className='ri-price-tag-3-line'
                                aria-hidden='true'
                              />
                              Offer {activePartner.idOffer}
                            </span>
                          ) : null}
                          {procurementHeader ? (
                            <span className='neg-chip'>
                              {procurementHeader.isSpot ? 'Spot' : 'Term'}
                            </span>
                          ) : null}
                        </>
                      ) : null
                    }
                    badges={
                      detail ? (
                        <>
                          <NegotiationStatusBadge
                            code={
                              activePartner
                                ? getPartnerDisplayStatus(activePartner)
                                : detail.statusLabel
                            }
                          />
                          {procurementHeader?.updatedDate ? (
                            <span className='text-muted small d-inline-flex align-items-center gap-1'>
                              <i className='ri-time-line' aria-hidden='true' />
                              Updated{' '}
                              {formatDateTime(procurementHeader.updatedDate)}
                            </span>
                          ) : null}
                        </>
                      ) : null
                    }
                    actions={
                      activeCargo?.startedAt ? (
                        <div
                          className={timerClass}
                          title={
                            deadlineText
                              ? `Deadline ${deadlineText}`
                              : 'Negotiation timer'
                          }
                        >
                          <i className='ri-timer-line' aria-hidden='true' />
                          <div>
                            <span className='neg-hero-timer__label'>Timer</span>
                            <strong className='neg-hero-timer__value'>
                              {!timerRunning
                                ? 'Done'
                                : timerExpired
                                  ? 'Expired'
                                  : formatTimerDisplay(timerLeft)}
                            </strong>
                            {deadlineText && timerRunning && (
                              <span className='neg-hero-timer__deadline'>
                                {deadlineText}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : null
                    }
                    facts={detail ? headerFacts : []}
                  />
                </div>

                {isLoading && !detail ? (
                  <SkeletonTable rows={3} columns={7} />
                ) : !detail ? (
                  <EmptyState
                    icon='ri-inbox-line'
                    title='Negotiation not found'
                    emptyHint='This negotiation could not be loaded. It may have been removed, or you may not have access to it.'
                    action={{
                      label: 'Back to list',
                      icon: 'ri-arrow-left-line',
                      onClick: handleBack,
                    }}
                  />
                ) : activePartner ? (
                  <NegotiationRoom
                    cargo={activeCargo}
                    partner={activePartner}
                    procurement={{
                      ...(detail.procurement || {}),
                      ...(procurementHeader || {}),
                    }}
                    mode={mode}
                    nowMs={nowMs}
                    selectedLineId={lineByPartner[activePartner.id]}
                    onSelectLine={selectLine}
                    onSubmitRound={submitRound}
                    onAccept={acceptLine}
                    onWithdrawRound={withdrawRound}
                    onWithdraw={withdrawPartner}
                    onRefresh={refresh}
                  />
                ) : (
                  <NegotiationWorkspace
                    detail={detail}
                    mode={mode}
                    canStartTimer={canStartTimer}
                    nowMs={nowMs}
                    unit={priceUnitSuffix(procurementHeader?.productName)}
                    partnerFilter={partnerFilter}
                    onPartnerFilterChange={setPartnerFilter}
                    onStartCargo={startCargo}
                    onExtendCargo={extendCargo}
                    onOpenRoom={partner => setActivePartnerId(partner.id)}
                  />
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NegotiationDetail;
